import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Building2, Users } from "lucide-react";

const ASSET_TYPES = [
  "Property",
  "Vehicle",
  "Savings",
  "Investment Shares",
  "Credit Card",
  "Other Creditor",
  "Other",
];

// PR #116 / APP-7 — type-aware placeholders so each row's input boxes hint
// at what to enter for that specific asset/liability type. Keeps the row
// self-identifying without forcing Mark to scroll back to the type column.
function identifierPlaceholderFor(assetType) {
  switch (assetType) {
    case "Property":         return "Title reference / lot number";
    case "Vehicle":          return "Registration plate";
    case "Savings":          return "Account number / BSB";
    case "Investment Shares":return "Holder ID / SRN";
    case "Credit Card":      return "Card / account ref (last 4 ok)";
    case "Other Creditor":   return "Loan / account ref";
    default:                 return "Reference / account number";
  }
}

function descriptionPlaceholderFor(assetType) {
  switch (assetType) {
    case "Property":         return "Property address";
    case "Vehicle":          return "Make / model / year";
    case "Savings":          return "Bank name + product";
    case "Investment Shares":return "Holding name (e.g. CBA shares)";
    case "Credit Card":      return "Card name (e.g. NAB Platinum)";
    case "Other Creditor":   return "Lender / counterparty";
    default:                 return "Description";
  }
}

const emptyAsset = {
  asset_type: "",
  identifier: "",
  description: "",
  value: 0,
  amount_owing: 0,
  to_be_refinanced: false,
};

export const StepAssetsLiabilities = ({ formData, updateFormData }) => {
  const [companyAssets, setCompanyAssets] = useState(
    formData.company_assets_liabilities.length > 0
      ? formData.company_assets_liabilities
      : ASSET_TYPES.map((type) => ({ ...emptyAsset, asset_type: type }))
  );
  
  const [guarantorAssets, setGuarantorAssets] = useState(
    formData.guarantor_assets_liabilities.length > 0
      ? formData.guarantor_assets_liabilities
      : ASSET_TYPES.map((type) => ({ ...emptyAsset, asset_type: type }))
  );

  const [activeTab, setActiveTab] = useState("company");

  useEffect(() => {
    updateFormData("company_assets_liabilities", companyAssets);
  }, [companyAssets, updateFormData]);

  useEffect(() => {
    updateFormData("guarantor_assets_liabilities", guarantorAssets);
  }, [guarantorAssets, updateFormData]);

  const handleAssetChange = (isCompany, index, field, value) => {
    const assets = isCompany ? companyAssets : guarantorAssets;
    const setAssets = isCompany ? setCompanyAssets : setGuarantorAssets;
    
    const newAssets = [...assets];
    newAssets[index] = { ...newAssets[index], [field]: value };
    setAssets(newAssets);
  };

  const addAsset = (isCompany) => {
    const assets = isCompany ? companyAssets : guarantorAssets;
    const setAssets = isCompany ? setCompanyAssets : setGuarantorAssets;
    setAssets([...assets, { ...emptyAsset, asset_type: "Other" }]);
  };

  const removeAsset = (isCompany, index) => {
    const assets = isCompany ? companyAssets : guarantorAssets;
    const setAssets = isCompany ? setCompanyAssets : setGuarantorAssets;
    if (assets.length > 1) {
      setAssets(assets.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = (assets) => {
    return assets.reduce(
      (acc, asset) => ({
        totalValue: acc.totalValue + (parseFloat(asset.value) || 0),
        totalOwing: acc.totalOwing + (parseFloat(asset.amount_owing) || 0),
      }),
      { totalValue: 0, totalOwing: 0 }
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const renderAssetTable = (assets, isCompany) => {
    const totals = calculateTotals(assets);
    const netPosition = totals.totalValue - totals.totalOwing;

    return (
      <div className="space-y-6">
        {/* Desktop Table View */}
        {/* APPLY-3 (2026-07-08) — visible affordance for narrow screens where
            the table overflows sideways (MED: no scroll hint). */}
        <p className="hidden md:block xl:hidden text-[11px] text-gray-400 text-right mb-1 select-none">
          Scroll sideways to see all columns →
        </p>
        <div className="hidden md:block overflow-x-auto">
          <table className="data-table w-full" data-testid={`${isCompany ? 'company' : 'guarantor'}-assets-table`}>
            <thead>
              <tr>
                <th className="w-32">Asset Type</th>
                <th className="w-32">Identifier</th>
                <th>Description / Address</th>
                <th className="w-40 text-right">Value ($)</th>
                <th className="w-40 text-right">Amount Owing ($)</th>
                <th className="w-28 text-center">Refinance</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset, index) => (
                <tr key={index} data-testid={`${isCompany ? 'company' : 'guarantor'}-asset-row-${index}`}>
                  <td>
                    {/* PR #116 / APP-7 — Asset type is the row identity. Render
                        as a navy chip so it's never ambiguous which row you're
                        editing when you're deep in the value/owing columns. */}
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold text-[#021d49] bg-[#021d49]/10 border border-[#021d49]/20 whitespace-nowrap">
                      {asset.asset_type || "—"}
                    </span>
                  </td>
                  <td>
                    <Input
                      value={asset.identifier || ""}
                      onChange={(e) => handleAssetChange(isCompany, index, "identifier", e.target.value)}
                      placeholder={identifierPlaceholderFor(asset.asset_type)}
                      className="form-input text-sm"
                      data-testid={`${isCompany ? 'company' : 'guarantor'}-asset-identifier-${index}`}
                    />
                  </td>
                  <td>
                    <Input
                      value={asset.description}
                      onChange={(e) => handleAssetChange(isCompany, index, "description", e.target.value)}
                      placeholder={descriptionPlaceholderFor(asset.asset_type)}
                      className="form-input text-sm"
                      data-testid={`${isCompany ? 'company' : 'guarantor'}-asset-desc-${index}`}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={asset.value || ""}
                      onChange={(e) => handleAssetChange(isCompany, index, "value", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="form-input text-sm text-right"
                      data-testid={`${isCompany ? 'company' : 'guarantor'}-asset-value-${index}`}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={asset.amount_owing || ""}
                      onChange={(e) => handleAssetChange(isCompany, index, "amount_owing", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="form-input text-sm text-right"
                      data-testid={`${isCompany ? 'company' : 'guarantor'}-asset-owing-${index}`}
                    />
                  </td>
                  <td className="text-center">
                    <Checkbox
                      checked={asset.to_be_refinanced}
                      onCheckedChange={(v) => handleAssetChange(isCompany, index, "to_be_refinanced", v)}
                      data-testid={`${isCompany ? 'company' : 'guarantor'}-asset-refinance-${index}`}
                    />
                  </td>
                  <td>
                    {index >= ASSET_TYPES.length && (
                      <button
                        onClick={() => removeAsset(isCompany, index)}
                        className="text-red-500 hover:text-red-700 p-1"
                        data-testid={`${isCompany ? 'company' : 'guarantor'}-remove-asset-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 font-semibold">
                <td colSpan="3" className="text-right">Totals:</td>
                <td className="text-right text-[#021d49]">{formatCurrency(totals.totalValue)}</td>
                <td className="text-right text-[#021d49]">{formatCurrency(totals.totalOwing)}</td>
                <td colSpan="2"></td>
              </tr>
              <tr className="bg-[#021d49] text-white font-semibold">
                <td colSpan="3" className="text-right">Net Position:</td>
                <td colSpan="2" className="text-center text-[#bd9a59] text-lg">
                  {formatCurrency(netPosition)}
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {assets.map((asset, index) => (
            <div 
              key={index} 
              className="form-card rounded p-4"
              data-testid={`${isCompany ? 'company' : 'guarantor'}-asset-card-${index}`}
            >
              <div className="flex justify-between items-center mb-3">
                {/* PR #116 / APP-7 — chip-style type label so the entry stays
                    self-identifying on small screens too. */}
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold text-[#021d49] bg-[#021d49]/10 border border-[#021d49]/20">
                  {asset.asset_type || "—"}
                </span>
                {index >= ASSET_TYPES.length && (
                  <button
                    onClick={() => removeAsset(isCompany, index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="form-label text-xs">Identifier</Label>
                  <Input
                    value={asset.identifier || ""}
                    onChange={(e) => handleAssetChange(isCompany, index, "identifier", e.target.value)}
                    placeholder={identifierPlaceholderFor(asset.asset_type)}
                    className="form-input text-sm"
                  />
                </div>

                <div>
                  <Label className="form-label text-xs">Description / Address</Label>
                  <Input
                    value={asset.description}
                    onChange={(e) => handleAssetChange(isCompany, index, "description", e.target.value)}
                    placeholder={descriptionPlaceholderFor(asset.asset_type)}
                    className="form-input text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="form-label text-xs">Value ($)</Label>
                    <Input
                      type="number"
                      value={asset.value || ""}
                      onChange={(e) => handleAssetChange(isCompany, index, "value", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="form-input text-sm text-right"
                    />
                  </div>
                  <div>
                    <Label className="form-label text-xs">Amount Owing ($)</Label>
                    <Input
                      type="number"
                      value={asset.amount_owing || ""}
                      onChange={(e) => handleAssetChange(isCompany, index, "amount_owing", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="form-input text-sm text-right"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={asset.to_be_refinanced}
                    onCheckedChange={(v) => handleAssetChange(isCompany, index, "to_be_refinanced", v)}
                  />
                  <Label className="text-sm">To be refinanced</Label>
                </div>
              </div>
            </div>
          ))}

          {/* Mobile Totals */}
          <div className="form-card rounded p-4 bg-gray-100">
            <div className="flex justify-between mb-2">
              <span className="font-medium">Total Value:</span>
              <span className="font-semibold text-[#021d49]">{formatCurrency(totals.totalValue)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-medium">Total Owing:</span>
              <span className="font-semibold text-[#021d49]">{formatCurrency(totals.totalOwing)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-300">
              <span className="font-bold">Net Position:</span>
              <span className="font-bold text-[#bd9a59] text-lg">{formatCurrency(netPosition)}</span>
            </div>
          </div>
        </div>

        {/* Add Asset Button */}
        <button
          onClick={() => addAsset(isCompany)}
          className="btn-secondary px-4 py-2 rounded flex items-center gap-2 text-sm"
          data-testid={`${isCompany ? 'company' : 'guarantor'}-add-asset-btn`}
        >
          <Plus className="w-4 h-4" />
          Add Additional Asset/Liability
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500 mb-6">
        Please provide details of all assets and liabilities for both the company borrower and guarantors.
        Tick the checkbox if the asset is to be refinanced as part of this application.
      </p>

      {/* HF-Apply-10 (2026-06-24) — replaced tabs with two sequential
          sections. The previous Tabs widget hid the Guarantor section
          behind a click, and Mark confirmed multiple clients submitted
          with empty guarantor data because they never realised the tab
          existed. Sequential cards force both to be visible. */}
      <div className="form-card rounded p-6">
        <h3 className="section-subheader flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#bd9a59]" />
          Company Borrower — Assets &amp; Liabilities
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Assets and liabilities held by the borrowing company.
        </p>
        {renderAssetTable(companyAssets, true)}
      </div>

      <div className="form-card rounded p-6 mt-8 border-t-4 border-[#bd9a59]/30">
        <h3 className="section-subheader flex items-center gap-2">
          <Users className="w-5 h-5 text-[#bd9a59]" />
          Guarantor(s) — Assets &amp; Liabilities
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Combined personal assets and liabilities for ALL guarantors listed in this application.
          Don't forget to add this section even if the company section above is complete.
        </p>
        {renderAssetTable(guarantorAssets, false)}
      </div>
    </div>
  );
};
